interface SegmentedToggleOption<T extends string | number> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

interface SegmentedToggleProps<T extends string | number> {
  value: T;
  options: SegmentedToggleOption<T>[];
  onChange: (v: T) => void;
  className?: string;
  /** When true, the control stretches to fill its container with each segment of equal width. */
  fullWidth?: boolean;
}

/**
 * Horizontal pill segmented control. Used where a small set of mutually-exclusive
 * options would otherwise be a vertical radio stack.
 */
const SegmentedToggle = <T extends string | number>({
  value,
  options,
  onChange,
  className = "",
  fullWidth = false,
}: SegmentedToggleProps<T>) => (
  <div
    className={`${
      fullWidth ? "flex w-full" : "inline-flex"
    } rounded-lg border border-grey-70 dark:border-grey-50 p-1 bg-grey-95 dark:bg-primary-10 ${className}`}
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={active}
          className={`${
            // Equal-width segments are tight for longer translations ("Dunkel"),
            // so full-width mode trades padding for label room.
            fullWidth ? "min-w-0 flex-1 justify-center gap-1 px-1" : "gap-1.5 px-2"
          } flex items-center py-1.5 text-sm font-medium rounded-md transition-colors ${
            active
              ? "bg-white not-dark:text-primary-40 shadow-sm dark:bg-primary-30 dark:text-primary-90"
              : "text-grey-30 hover:text-grey-20 dark:text-grey-70 dark:hover:text-white"
          }`}
        >
          {opt.icon}
          <span className="truncate">{opt.label}</span>
        </button>
      );
    })}
  </div>
);

export default SegmentedToggle;
